import React from 'react'
import styled from '@emotion/styled'
import { MerchantLocation } from '@slerp/controls'
import { HiExternalLink } from 'react-icons/hi'
import { Section } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import { DeleteButton } from '../styles'
import {
  ActionCard,
  OutlineButton,
  PrimaryButton,
  TableWrapper,
  TableHeaderBar
} from './styles'
import { LEAT_APP_URL } from '../../constants'
import {
  AUTH_STEP_TITLE,
  AUTH_STEP_DESCRIPTION,
  EXTERNAL_LINK_PROPS
} from './copy'
import { ConnectStoresRow, LeatStore, LeatStoreLink } from './types'
import { isLeatConnected } from './utils'
import StoreListStep from './StoreListStep'

interface StoreLinkingStepProps {
  activeStores: MerchantLocation[]
  leatStores: LeatStore[]
  links: LeatStoreLink[]
  loading?: boolean
  onRecheck: () => void
  onDisconnect: () => void
  onOpenConnectForm: () => void
  onSaveStoreList: (rows: ConnectStoresRow[]) => void
}

const StoreLinkingStep = ({
  activeStores,
  leatStores,
  links,
  loading,
  onRecheck,
  onDisconnect,
  onOpenConnectForm,
  onSaveStoreList
}: StoreLinkingStepProps) => {
  const hasLeatShops = leatStores.length > 0
  const connectedCount = activeStores.filter((store) =>
    isLeatConnected(store.id, links)
  ).length

  // Disconnect belongs to the token/auth step, not to whether any store
  // happens to be linked yet — it must stay reachable in every state.
  const authSection = (
    <Section
      title={AUTH_STEP_TITLE}
      description={AUTH_STEP_DESCRIPTION}
      stepStatus={StepStatus.completed}
      stepNumber={1}
      action={
        <DeleteButton disabled={loading} onClick={onDisconnect}>
          Disconnect
        </DeleteButton>
      }
    />
  )

  if (connectedCount > 0) {
    return (
      <>
        {authSection}
        <StoreListStep
          activeStores={activeStores}
          links={links}
          leatShops={leatStores}
          loading={loading}
          onSave={onSaveStoreList}
          onOpenConnectForm={onOpenConnectForm}
        />
      </>
    )
  }

  // Neither branch has data to connect yet — no Leat shops, or no active
  // Slerp store linked — so both stay informational.
  const sectionTitle = hasLeatShops
    ? "We couldn't find any connected Slerp stores in your account"
    : "We couldn't find any shops in your Leat account"
  const sectionDescription = hasLeatShops
    ? "Your token works — you just don't have any shops set up yet"
    : "Your token works — Leat just has no shops set up yet. Create one for each Slerp store that should offer loyalty, then come back here and we'll pick up where you left off."

  return (
    <>
      {authSection}

      <Section
        title='Link your stores'
        description='Choose which Leat shop each Slerp store belongs to. The integration will only work for stores that are mapped between Slerp and Leat.'
        stepStatus={StepStatus.active}
        stepNumber={2}
      >
        <TableWrapper>
          <TableHeaderBar />
          <EmptyContent>
            <EmptyText>
              <EmptyTitle>{sectionTitle}</EmptyTitle>
              <EmptyDescription>{sectionDescription}</EmptyDescription>
            </EmptyText>
            {hasLeatShops && (
              <PrimaryButton
                disabled={activeStores.length === 0}
                onClick={onOpenConnectForm}
              >
                LINK STORES
              </PrimaryButton>
            )}
            {!hasLeatShops && (
              <OutlineButton href={LEAT_APP_URL} {...EXTERNAL_LINK_PROPS}>
                CREATE SHOPS IN LEAT <HiExternalLink />
              </OutlineButton>
            )}
          </EmptyContent>
        </TableWrapper>
      </Section>

      {!hasLeatShops && (
        <ActionCard>
          <OutlineButton loading={loading} onClick={onRecheck}>
            RE-CHECK FOR SHOPS
          </OutlineButton>
          <PrimaryButton disabled>CONFIRM &amp; CONNECT</PrimaryButton>
        </ActionCard>
      )}
    </>
  )
}

const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 32px;
  text-align: center;
`

const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
`

const EmptyTitle = styled.strong`
  font-size: 16px;
  font-weight: 600;
  color: #4a4a68;
`

const EmptyDescription = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #4a4a68;
`

export default StoreLinkingStep
